import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getWeightEntries, createWeightEntry, deleteWeightEntry, getFighters, trackFeatureUsage, getWeightGoal, createWeightGoal } from '../services/api';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays, addDays } from 'date-fns';
import { 
  Scale,
  Plus,
  Trash2,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  Target,
  Calendar,
  Activity,
  Percent,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Edit
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Area,
  AreaChart,
  ComposedChart,
  Bar
} from 'recharts';
import { cn } from '../lib/utils';

export const WeightTrackingPage = () => {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedFighter = searchParams.get('fighter');
  
  const [entries, setEntries] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFighter, setSelectedFighter] = useState(preselectedFighter || 'all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    fighter_id: preselectedFighter || '',
    weight: '',
    body_fat: '',
    notes: ''
  });
  const [goalData, setGoalData] = useState({
    target_weight: '',
    goal_date: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [weightGoals, setWeightGoals] = useState({});

  useEffect(() => {
    loadData();
    trackFeatureUsage('weight_tracking', 'view').catch(() => {});
  }, [selectedFighter]);

  const loadData = async () => {
    try {
      const fighterId = selectedFighter !== 'all' ? selectedFighter : null;
      const [entriesRes, fightersRes] = await Promise.all([
        getWeightEntries(fighterId),
        getFighters()
      ]);
      setEntries(entriesRes.data);
      setFighters(fightersRes.data);
      
      // Load weight goals for selected fighter
      if (fighterId) {
        try {
          const goalsRes = await getWeightGoal(fighterId);
          setWeightGoals(goalsRes.data || {});
        } catch (e) {
          setWeightGoals({});
        }
      }
    } catch (error) {
      toast.error('Failed to load weight data');
    } finally {
      setLoading(false);
    }
  };

  const getFighterName = (fighterId) => {
    const fighter = fighters.find(f => f.id === fighterId);
    return fighter?.name || 'Unknown';
  };

  const getSelectedFighter = () => {
    return fighters.find(f => f.id === selectedFighter);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createWeightEntry({
        fighter_id: formData.fighter_id,
        weight: parseFloat(formData.weight),
        body_fat: formData.body_fat ? parseFloat(formData.body_fat) : null,
        notes: formData.notes || null
      });
      toast.success('Weight recorded');
      setDialogOpen(false);
      setFormData({ fighter_id: '', weight: '', body_fat: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to record weight');
    } finally {
      setSaving(false);
    }
  };

  const handleGoalSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createWeightGoal({
        fighter_id: selectedFighter,
        target_weight: parseFloat(goalData.target_weight),
        goal_date: goalData.goal_date,
        notes: goalData.notes || null
      });
      toast.success('Weight goal set');
      setGoalDialogOpen(false);
      setGoalData({ target_weight: '', goal_date: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to set weight goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteWeightEntry(id);
      toast.success('Entry deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  // Prepare chart data with projection
  const getChartData = () => {
    if (selectedFighter === 'all' || entries.length === 0) return null;
    
    const data = entries
      .slice(0, 30)
      .reverse()
      .map(e => ({
        date: format(parseISO(e.created_at), 'MMM d'),
        weight: e.weight,
        body_fat: e.body_fat || null,
        fullDate: e.created_at
      }));
    
    // Add projection if we have a goal
    if (weightGoals.target_weight && weightGoals.goal_date && data.length > 0) {
      const currentWeight = data[data.length - 1].weight;
      const targetWeight = weightGoals.target_weight;
      const goalDate = parseISO(weightGoals.goal_date);
      const today = new Date();
      const daysToGoal = differenceInDays(goalDate, today);
      
      if (daysToGoal > 0) {
        const weightPerDay = (currentWeight - targetWeight) / daysToGoal;
        
        // Add projection points (every 7 days)
        for (let i = 7; i <= daysToGoal; i += 7) {
          const projDate = addDays(today, i);
          data.push({
            date: format(projDate, 'MMM d'),
            projected: currentWeight - (weightPerDay * i),
            target: targetWeight
          });
        }
        
        // Add final goal point
        data.push({
          date: format(goalDate, 'MMM d'),
          projected: targetWeight,
          target: targetWeight
        });
      }
    }
    
    return data;
  };

  const chartData = getChartData();

  // Calculate stats
  const getStats = () => {
    if (entries.length === 0) return null;
    
    const currentWeight = entries[0]?.weight;
    const targetWeight = weightGoals.target_weight;
    const goalDate = weightGoals.goal_date ? parseISO(weightGoals.goal_date) : null;
    
    // Weight change (last 7 days)
    const weekAgoEntries = entries.filter(e => {
      const entryDate = parseISO(e.created_at);
      const weekAgo = addDays(new Date(), -7);
      return entryDate >= weekAgo;
    });
    const weekChange = weekAgoEntries.length >= 2 
      ? weekAgoEntries[weekAgoEntries.length - 1].weight - weekAgoEntries[0].weight 
      : 0;
    
    // Days to goal
    const daysToGoal = goalDate ? differenceInDays(goalDate, new Date()) : null;
    
    // Weight to lose/gain
    const weightToGo = targetWeight ? currentWeight - targetWeight : null;
    
    // Required rate (lbs per week)
    const requiredRate = (weightToGo && daysToGoal && daysToGoal > 0) 
      ? (weightToGo / daysToGoal) * 7 
      : null;
    
    // Progress percentage
    const startWeight = entries.length > 0 ? entries[entries.length - 1].weight : currentWeight;
    const progressPercent = targetWeight && startWeight !== targetWeight
      ? Math.min(100, Math.max(0, ((startWeight - currentWeight) / (startWeight - targetWeight)) * 100))
      : 0;
    
    return {
      currentWeight,
      targetWeight,
      weekChange,
      daysToGoal,
      weightToGo,
      requiredRate,
      progressPercent,
      startWeight
    };
  };

  const stats = getStats();

  // Get weight trend
  const getTrend = (index) => {
    if (index >= entries.length - 1) return null;
    const current = entries[index].weight;
    const previous = entries[index + 1].weight;
    if (current < previous) return 'down';
    if (current > previous) return 'up';
    return 'same';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const fighter = getSelectedFighter();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="weight-tracking-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Weight Tracking
            </h1>
            <p className="text-zinc-400 mt-1">Monitor fighter weight for cuts and camps</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && selectedFighter !== 'all' && (
              <Button 
                onClick={() => setGoalDialogOpen(true)}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="set-goal-btn"
              >
                <Target className="w-4 h-4 mr-2" />
                Set Goal
              </Button>
            )}
            {isAdmin && (
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold tracking-wide"
                data-testid="add-weight-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Record Weight
              </Button>
            )}
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-4">
          <Select value={selectedFighter} onValueChange={setSelectedFighter}>
            <SelectTrigger className="w-64 h-12 bg-zinc-900 border-zinc-800" data-testid="fighter-filter">
              <SelectValue placeholder="Select fighter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Fighters</SelectItem>
              {fighters.map(fighter => (
                <SelectItem key={fighter.id} value={fighter.id}>
                  {fighter.name} - {fighter.weight_class}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats Dashboard - Only show when fighter is selected */}
        {selectedFighter !== 'all' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Current Weight */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-4 h-4 text-zinc-500" />
                <span className="text-xs text-zinc-500 uppercase">Current</span>
              </div>
              <p className="font-heading text-2xl font-bold text-white">
                {stats.currentWeight}<span className="text-sm text-zinc-500 ml-1">lbs</span>
              </p>
            </div>

            {/* Target Weight */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-red-500" />
                <span className="text-xs text-zinc-500 uppercase">Target</span>
              </div>
              <p className="font-heading text-2xl font-bold text-white">
                {stats.targetWeight ? (
                  <>{stats.targetWeight}<span className="text-sm text-zinc-500 ml-1">lbs</span></>
                ) : (
                  <span className="text-zinc-500 text-lg">Not set</span>
                )}
              </p>
            </div>

            {/* Weight to Go */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                {stats.weightToGo > 0 ? (
                  <TrendingDown className="w-4 h-4 text-green-500" />
                ) : (
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                )}
                <span className="text-xs text-zinc-500 uppercase">To Go</span>
              </div>
              <p className={cn(
                "font-heading text-2xl font-bold",
                stats.weightToGo > 0 ? "text-green-500" : stats.weightToGo < 0 ? "text-blue-500" : "text-zinc-500"
              )}>
                {stats.weightToGo ? (
                  <>{Math.abs(stats.weightToGo).toFixed(1)}<span className="text-sm text-zinc-500 ml-1">lbs</span></>
                ) : (
                  <span className="text-zinc-500 text-lg">-</span>
                )}
              </p>
            </div>

            {/* Days Remaining */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-yellow-500" />
                <span className="text-xs text-zinc-500 uppercase">Days Left</span>
              </div>
              <p className={cn(
                "font-heading text-2xl font-bold",
                stats.daysToGoal && stats.daysToGoal < 7 ? "text-red-500" : "text-white"
              )}>
                {stats.daysToGoal !== null ? stats.daysToGoal : <span className="text-zinc-500 text-lg">-</span>}
              </p>
            </div>

            {/* Weekly Change */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-purple-500" />
                <span className="text-xs text-zinc-500 uppercase">Week Change</span>
              </div>
              <p className={cn(
                "font-heading text-2xl font-bold",
                stats.weekChange < 0 ? "text-green-500" : stats.weekChange > 0 ? "text-red-500" : "text-zinc-500"
              )}>
                {stats.weekChange !== 0 ? (
                  <>{stats.weekChange > 0 ? '+' : ''}{stats.weekChange.toFixed(1)}<span className="text-sm text-zinc-500 ml-1">lbs</span></>
                ) : (
                  <span className="text-zinc-500 text-lg">0</span>
                )}
              </p>
            </div>

            {/* Required Rate */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-orange-500" />
                <span className="text-xs text-zinc-500 uppercase">Rate/Week</span>
              </div>
              <p className={cn(
                "font-heading text-2xl font-bold",
                stats.requiredRate && stats.requiredRate > 2 ? "text-red-500" : "text-white"
              )}>
                {stats.requiredRate ? (
                  <>
                    {stats.requiredRate.toFixed(1)}<span className="text-sm text-zinc-500 ml-1">lbs</span>
                    {stats.requiredRate > 2 && <AlertTriangle className="w-4 h-4 text-red-500 inline ml-1" />}
                  </>
                ) : (
                  <span className="text-zinc-500 text-lg">-</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Progress Bar - Only show when fighter and goal are set */}
        {selectedFighter !== 'all' && stats?.targetWeight && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight">
                Weight Cut Progress
              </h3>
              <span className="text-2xl font-heading font-bold text-red-500">
                {stats.progressPercent.toFixed(0)}%
              </span>
            </div>
            <div className="h-4 bg-zinc-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stats.progressPercent}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                className={cn(
                  "h-full rounded-full",
                  stats.progressPercent >= 100 ? "bg-green-500" :
                  stats.progressPercent >= 75 ? "bg-yellow-500" :
                  "bg-red-600"
                )}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm text-zinc-500">
              <span>Start: {stats.startWeight} lbs</span>
              <span>Target: {stats.targetWeight} lbs</span>
            </div>
          </div>
        )}

        {/* Weight Chart with Projection */}
        {chartData && chartData.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
          >
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4">
              Weight History & Projection
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                  <YAxis 
                    domain={['dataMin - 5', 'dataMax + 5']} 
                    tick={{ fill: '#a1a1aa', fontSize: 12 }}
                    tickFormatter={(value) => `${value}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0,0,0,0.9)', 
                      border: '1px solid #dc2626',
                      borderRadius: '4px'
                    }}
                    formatter={(value, name) => [
                      `${value?.toFixed(1)} lbs`, 
                      name === 'weight' ? 'Actual' : name === 'projected' ? 'Projected' : 'Target'
                    ]}
                  />
                  {weightGoals.target_weight && (
                    <ReferenceLine 
                      y={weightGoals.target_weight} 
                      stroke="#22c55e" 
                      strokeDasharray="5 5" 
                      label={{ value: 'Target', fill: '#22c55e', fontSize: 12, position: 'right' }}
                    />
                  )}
                  <Line 
                    type="monotone" 
                    dataKey="weight" 
                    stroke="#dc2626" 
                    strokeWidth={2} 
                    dot={{ fill: '#dc2626', r: 4 }}
                    connectNulls
                  />
                  <Line 
                    type="monotone" 
                    dataKey="projected" 
                    stroke="#f59e0b" 
                    strokeWidth={2} 
                    strokeDasharray="5 5"
                    dot={{ fill: '#f59e0b', r: 3 }}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-red-600" />
                <span className="text-xs text-zinc-400">Actual Weight</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-yellow-500" style={{ borderStyle: 'dashed' }} />
                <span className="text-xs text-zinc-400">Projected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-green-500" style={{ borderStyle: 'dashed' }} />
                <span className="text-xs text-zinc-400">Target</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Entries List */}
        {entries.length > 0 ? (
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight">
              Weight Log
            </h3>
            {entries.map((entry, index) => {
              const trend = getTrend(index);
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors"
                  data-testid={`weight-entry-${entry.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-12 h-12 rounded-sm flex items-center justify-center",
                        trend === 'down' ? "bg-green-600/20" : 
                        trend === 'up' ? "bg-red-600/20" : "bg-zinc-800"
                      )}>
                        {trend === 'down' && <TrendingDown className="w-6 h-6 text-green-500" />}
                        {trend === 'up' && <TrendingUp className="w-6 h-6 text-red-500" />}
                        {trend === 'same' && <Minus className="w-6 h-6 text-zinc-500" />}
                        {trend === null && <Scale className="w-6 h-6 text-zinc-500" />}
                      </div>
                      <div>
                        <p className="text-white font-medium flex items-center gap-2">
                          <Users className="w-4 h-4 text-zinc-500" />
                          {getFighterName(entry.fighter_id)}
                        </p>
                        <p className="text-zinc-500 text-sm">
                          {format(parseISO(entry.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="font-heading text-2xl font-bold text-white tabular-nums">
                          {entry.weight} <span className="text-sm text-zinc-500">lbs</span>
                        </p>
                        {entry.body_fat && (
                          <p className="text-zinc-400 text-sm flex items-center justify-end gap-1">
                            <Percent className="w-3 h-3" />
                            {entry.body_fat}% body fat
                          </p>
                        )}
                        {entry.notes && (
                          <p className="text-zinc-500 text-sm max-w-xs truncate">{entry.notes}</p>
                        )}
                      </div>
                      
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(entry.id)}
                          className="text-zinc-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
            <Scale className="w-12 h-12 mb-4 opacity-50" />
            <p className="text-lg mb-2">No weight entries yet</p>
            <p className="text-sm">Start tracking fighter weights for better camp management</p>
          </div>
        )}

        {/* Add Weight Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase tracking-tight">
                Record Weight
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Fighter *</Label>
                <Select 
                  value={formData.fighter_id} 
                  onValueChange={(value) => setFormData({ ...formData, fighter_id: value })}
                >
                  <SelectTrigger className="h-12 bg-zinc-950 border-zinc-800">
                    <SelectValue placeholder="Select fighter" />
                  </SelectTrigger>
                  <SelectContent>
                    {fighters.map(fighter => (
                      <SelectItem key={fighter.id} value={fighter.id}>
                        {fighter.name} - {fighter.weight_class}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Weight (lbs) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    placeholder="185.5"
                    className="h-12 bg-zinc-950 border-zinc-800"
                    required
                    data-testid="weight-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Body Fat % (optional)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.body_fat}
                    onChange={(e) => setFormData({ ...formData, body_fat: e.target.value })}
                    placeholder="12.5"
                    className="h-12 bg-zinc-950 border-zinc-800"
                    data-testid="body-fat-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Morning weigh-in, after cardio..."
                  className="bg-zinc-950 border-zinc-800 min-h-[80px]"
                  data-testid="weight-notes-input"
                />
              </div>
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-zinc-700 text-zinc-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !formData.fighter_id || !formData.weight}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="save-weight-btn"
                >
                  {saving ? 'Saving...' : 'Record'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Set Weight Goal Dialog */}
        <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase tracking-tight flex items-center gap-2">
                <Target className="w-5 h-5 text-red-500" />
                Set Weight Goal
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleGoalSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Target Weight (lbs) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={goalData.target_weight}
                  onChange={(e) => setGoalData({ ...goalData, target_weight: e.target.value })}
                  placeholder="170"
                  className="h-12 bg-zinc-950 border-zinc-800"
                  required
                  data-testid="target-weight-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Goal Date *</Label>
                <Input
                  type="date"
                  value={goalData.goal_date}
                  onChange={(e) => setGoalData({ ...goalData, goal_date: e.target.value })}
                  className="h-12 bg-zinc-950 border-zinc-800"
                  required
                  data-testid="goal-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Notes</Label>
                <Textarea
                  value={goalData.notes}
                  onChange={(e) => setGoalData({ ...goalData, notes: e.target.value })}
                  placeholder="Fight weight for UFC 310..."
                  className="bg-zinc-950 border-zinc-800 min-h-[80px]"
                  data-testid="goal-notes-input"
                />
              </div>
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setGoalDialogOpen(false)}
                  className="border-zinc-700 text-zinc-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !goalData.target_weight || !goalData.goal_date}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="save-goal-btn"
                >
                  {saving ? 'Saving...' : 'Set Goal'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default WeightTrackingPage;
